import { useEffect, useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Link2, Unplug, Send, CalendarPlus, AlertTriangle } from "lucide-react";

export default function MicrosoftIntegration() {
  const { data: status, reload } = useApi("/integrations/microsoft/status");
  const [f, setF] = useState({ tenant_id: "", client_id: "", client_secret: "", teams: true, calendar: true, mail: false });
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState(null);
  const [sel, setSel] = useState({ team: "", channel: "" });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("microsoft");
    if (p === "connected") { toast.success("Microsoft 365 anslutet"); reload(); }
    else if (p === "error") toast.error("Microsoft-anslutningen misslyckades – kontrollera uppgifterna och försök igen");
    if (p) window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    const caps = [f.teams && "teams", f.calendar && "calendar", f.mail && "mail"].filter(Boolean);
    if (!f.tenant_id || !f.client_id || !f.client_secret) return toast.error("Fyll i Tenant ID, Client ID och Client Secret");
    setBusy(true);
    try {
      const r = await api.post("/integrations/microsoft/connect", { ...f, capabilities: caps });
      window.location.assign(r.data.authorization_url);
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Koppla bort Microsoft 365? Lagrade uppgifter raderas.")) return;
    try { await api.post("/integrations/microsoft/disconnect"); setTeams(null); reload(); toast.success("Bortkopplad"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const loadTeams = async () => {
    try { const r = await api.get("/integrations/microsoft/teams"); setTeams(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveChannel = async () => {
    const team = (teams || []).find((t) => t.team_id === sel.team);
    const chan = team?.channels.find((c) => c.id === sel.channel);
    if (!team || !chan) return toast.error("Välj team och kanal");
    try {
      await api.put("/integrations/microsoft/config", {
        teams_team_id: team.team_id, teams_team_name: team.team_name,
        teams_channel_id: chan.id, teams_channel_name: chan.name,
      });
      reload(); toast.success("Kanal sparad");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleCfg = async (key, val) => { try { await api.put("/integrations/microsoft/config", { [key]: val }); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const test = async () => { try { await api.post("/integrations/microsoft/test"); toast.success("Testmeddelande skickat till Teams"); } catch (e) { toast.error(apiErr(e)); } };
  const calTest = async () => { try { await api.post("/integrations/microsoft/calendar/test"); toast.success("Testhändelse skapad i kalendern"); } catch (e) { toast.error(apiErr(e)); } };

  const connected = status?.connected;

  return (
    <Card className="p-4" data-testid="microsoft-integration">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-[#2f2f8f] text-xs font-bold text-white">M365</span>
        <div className="flex-1">
          <div className="font-head font-semibold">Microsoft 365 (Teams & Outlook)</div>
          <div className="text-xs text-muted-foreground">Koppla organisationens egen Azure-app. Valfritt – hoppa över om ni inte vill synka.</div>
        </div>
        {connected
          ? <Badge className="gap-1 bg-emerald-600"><CheckCircle2 className="h-3 w-3" />Ansluten</Badge>
          : <Badge variant="outline">Ej ansluten</Badge>}
      </div>

      {status?.reauthorize && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
          <AlertTriangle className="h-4 w-4" />Behörigheten har upphört – anslut på nytt.
        </div>
      )}

      {!connected ? (
        <div className="space-y-3" data-testid="ms-connect-form">
          <div className="rounded-md bg-secondary p-3 text-xs text-muted-foreground">
            Er IT-admin registrerar en app i Microsoft Entra och lägger till redirect-URI:
            <code className="mt-1 block break-all rounded bg-background px-2 py-1 font-mono">{window.location.origin}/api/integrations/microsoft/callback</code>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><Label>Tenant ID</Label><Input value={f.tenant_id} onChange={(e) => setF({ ...f, tenant_id: e.target.value })} data-testid="ms-tenant" /></div>
            <div><Label>Client ID</Label><Input value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="ms-client" /></div>
            <div><Label>Client Secret</Label><Input type="password" value={f.client_secret} onChange={(e) => setF({ ...f, client_secret: e.target.value })} data-testid="ms-secret" /></div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><Checkbox checked={f.teams} onCheckedChange={(v) => setF({ ...f, teams: !!v })} data-testid="ms-cap-teams" />Teams-kanalutskick</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.calendar} onCheckedChange={(v) => setF({ ...f, calendar: !!v })} data-testid="ms-cap-calendar" />Outlook-kalender</label>
            <label className="flex items-center gap-2"><Checkbox checked={f.mail} onCheckedChange={(v) => setF({ ...f, mail: !!v })} data-testid="ms-cap-mail" />Outlook-mejl → Struktur</label>
          </div>
          <Button onClick={connect} disabled={busy} data-testid="ms-connect-btn"><Link2 className="mr-1 h-4 w-4" />Anslut Microsoft 365</Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="ms-connected">
          <div className="text-sm text-muted-foreground">Ansluten som <span className="font-medium text-foreground">{status.connected_account || "—"}</span> · tenant {status.tenant_id}</div>

          {status.capabilities?.includes("teams") && (
            <div className="rounded-lg border p-3">
              <div className="mb-2 text-sm font-semibold">Teams-kanal</div>
              {status.teams_channel_id && (
                <div className="mb-2 text-xs text-muted-foreground">Nuvarande: {status.teams_team_name} › {status.teams_channel_name}</div>
              )}
              {!teams ? (
                <Button size="sm" variant="outline" onClick={loadTeams} data-testid="ms-load-teams">Hämta team & kanaler</Button>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  <Select value={sel.team} onValueChange={(v) => setSel({ team: v, channel: "" })}>
                    <SelectTrigger data-testid="ms-team-select"><SelectValue placeholder="Välj team" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.team_id} value={t.team_id}>{t.team_name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={sel.channel} onValueChange={(v) => setSel({ ...sel, channel: v })} disabled={!sel.team}>
                    <SelectTrigger data-testid="ms-channel-select"><SelectValue placeholder="Välj kanal" /></SelectTrigger>
                    <SelectContent>{(teams.find((t) => t.team_id === sel.team)?.channels || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={saveChannel} data-testid="ms-save-channel">Spara kanal</Button>
                </div>
              )}
              <label className="mt-3 flex items-center gap-2 text-sm">
                <Checkbox checked={!!status.post_broadcasts} onCheckedChange={(v) => toggleCfg("post_broadcasts", !!v)} data-testid="ms-post-broadcasts" />
                Skicka utskick automatiskt till kanalen
              </label>
              {status.teams_channel_id && (
                <Button size="sm" variant="outline" className="mt-2" onClick={test} data-testid="ms-test-btn"><Send className="mr-1 h-4 w-4" />Skicka testmeddelande</Button>
              )}
            </div>
          )}

          {status.capabilities?.includes("calendar") && (
            <div className="rounded-lg border p-3">
              <div className="mb-2 text-sm font-semibold">Outlook-kalender</div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!status.calendar_enabled} onCheckedChange={(v) => toggleCfg("calendar_enabled", !!v)} data-testid="ms-calendar-enabled" />
                Aktivera kalenderhändelser
              </label>
              <Button size="sm" variant="outline" className="mt-2" onClick={calTest} data-testid="ms-cal-test-btn"><CalendarPlus className="mr-1 h-4 w-4" />Skapa testhändelse</Button>
            </div>
          )}

          <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnect} data-testid="ms-disconnect-btn"><Unplug className="mr-1 h-4 w-4" />Koppla bort</Button>
        </div>
      )}
    </Card>
  );
}
